// src/pages/Login.tsx
import { useState } from 'react'
import { supabase } from '../services/supabase'

type AuthMode = 'signin' | 'signup' | 'magic' | 'reset'

export default function LoginPage() {
  const [mode, setMode] = useState<AuthMode>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)

  const clear = (next: AuthMode) => { setError(null); setInfo(null); setSent(false); setMode(next) }

  const handleSignIn = async () => {
    if (!email || !password) { setError('Email and password are required'); return }
    setError(null); setLoading(true)
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password })
      if (error) setError(error.message)
    } catch { setError('Something went wrong') } finally { setLoading(false) }
  }

  const handleSignUp = async () => {
    if (!email || !password) { setError('Email and password are required'); return }
    if (password.length < 8) { setError('Password must be at least 8 characters'); return }
    setError(null); setLoading(true)
    try {
      const { error } = await supabase.auth.signUp({ email, password })
      if (error) setError(error.message)
      else setInfo('Check your email to confirm your account.')
    } catch { setError('Something went wrong') } finally { setLoading(false) }
  }

  const handleMagicLink = async () => {
    if (!email) { setError('Enter your email first'); return }
    setError(null); setLoading(true)
    try {
      const redirectTo = `${window.location.origin}/`
      const { error } = await supabase.auth.signInWithOtp({ email, options: { emailRedirectTo: redirectTo } })
      if (error) setError(error.message)
      else setSent(true)
    } catch { setError('Something went wrong') } finally { setLoading(false) }
  }

  const handleReset = async () => {
    if (!email) { setError('Enter your email first'); return }
    setError(null); setLoading(true)
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: `${window.location.origin}/` })
      if (error) setError(error.message)
      else setSent(true)
    } catch { setError('Something went wrong') } finally { setLoading(false) }
  }

  const handleGoogle = async () => {
    setError(null)
    try {
      const { error } = await supabase.auth.signInWithOAuth({ provider: 'google', options: { redirectTo: `${window.location.origin}/` } })
      if (error) setError(error.message)
    } catch { setError('Something went wrong') }
  }

  const primaryAction = mode === 'signin' ? handleSignIn : mode === 'signup' ? handleSignUp : mode === 'magic' ? handleMagicLink : handleReset
  const primaryLabel  = mode === 'signin' ? 'Sign In' : mode === 'signup' ? 'Create Account' : mode === 'magic' ? 'Send Magic Link' : 'Send Reset Link'

  return (
    <div className="auth-page bg-base" style={{ minHeight: '100dvh' }}>
      <div className="auth-box">
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--xl)' }}>
          <div className="logo-mark">L</div>
          <h1 className="display-md text-primary" style={{ marginBottom: 'var(--xs)' }}>Loqin</h1>
          <p className="body text-muted" style={{ textAlign: 'center' }}>A focus operating system for founders</p>
        </div>

        {/* ── Sent state ── */}
        {sent ? (
          <div className="animate-fade-slide-up" style={{ textAlign: 'center', padding: 'var(--xl) 0' }}>
            <div style={{ fontSize: 40, marginBottom: 'var(--md)' }}>📬</div>
            <p className="title text-signal" style={{ marginBottom: 'var(--sm)' }}>
              {mode === 'reset' ? 'Reset link sent' : 'Magic link sent'}
            </p>
            <p className="body text-muted" style={{ marginBottom: 'var(--xl)' }}>Check your inbox for {email}</p>
            <button className="btn btn-ghost" onClick={() => { setSent(false); setMode('signin') }} style={{ width: 'auto' }}>
              ← Back to sign in
            </button>
          </div>
        ) : (
          <>
            {/* Mode tabs */}
            {(mode === 'signin' || mode === 'signup') && (
              <div className="pill-tabs" style={{ marginBottom: 'var(--xl)' }}>
                {(['signin', 'signup'] as const).map(m => (
                  <button key={m} className={`pill-tab ${mode === m ? 'active' : ''}`} onClick={() => clear(m)}>
                    {m === 'signin' ? 'Sign In' : 'Sign Up'}
                  </button>
                ))}
              </div>
            )}

            {/* Magic / reset header */}
            {(mode === 'magic' || mode === 'reset') && (
              <div style={{ marginBottom: 'var(--lg)' }}>
                <button className="btn btn-ghost" style={{ width: 'auto', color: 'var(--accent)', padding: '0 0 var(--md)' }} onClick={() => clear('signin')}>
                  ← Back
                </button>
                <h2 className="title text-primary">{mode === 'magic' ? 'Magic Link' : 'Reset Password'}</h2>
                <p className="body-md text-muted" style={{ marginTop: 'var(--xs)' }}>
                  {mode === 'magic' ? "We'll email you a one-tap sign-in link — no password needed." : "Enter your email and we'll send a reset link."}
                </p>
              </div>
            )}

            {/* Email */}
            <div className="input-wrap">
              <label className="input-label" htmlFor="email">Email</label>
              <input
                id="email"
                className="input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="founder@company.com"
                autoComplete="email"
                onKeyDown={e => e.key === 'Enter' && primaryAction()}
              />
            </div>

            {/* Password */}
            {(mode === 'signin' || mode === 'signup') && (
              <div className="input-wrap">
                <label className="input-label" htmlFor="password">Password</label>
                <input
                  id="password"
                  className="input"
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                  onKeyDown={e => e.key === 'Enter' && primaryAction()}
                />
              </div>
            )}

            {/* Forgot */}
            {mode === 'signin' && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'calc(-1 * var(--md))', marginBottom: 'var(--lg)' }}>
                <button className="btn btn-ghost" style={{ width: 'auto', color: 'var(--accent)', padding: 'var(--xs) 0' }} onClick={() => clear('reset')}>
                  Forgot password?
                </button>
              </div>
            )}

            {/* Error / info */}
            {error && <p className="body-md" style={{ color: 'var(--noise)', marginBottom: 'var(--md)' }}>{error}</p>}
            {info  && <p className="body-md" style={{ color: 'var(--signal)', marginBottom: 'var(--md)' }}>{info}</p>}

            {/* CTA */}
            <button className="btn btn-primary" onClick={primaryAction} disabled={loading}>
              {loading ? <span className="spinner" /> : null}
              {loading ? 'Please wait…' : primaryLabel}
            </button>

            {/* Social / magic link options */}
            {(mode === 'signin' || mode === 'signup') && (
              <>
                <div className="divider"><div className="divider-line" /><span className="divider-text">OR</span><div className="divider-line" /></div>

                <button className="btn btn-outline" onClick={handleGoogle} style={{ marginBottom: 'var(--md)', gap: 'var(--sm)' }}>
                  <span style={{ fontSize: 16, fontWeight: 700, color: '#4285F4' }}>G</span>
                  Continue with Google
                </button>

                <div style={{ display: 'flex', justifyContent: 'center', gap: 4, marginTop: 'var(--md)', flexWrap: 'wrap' }}>
                  <span className="body-md text-muted">Prefer no password?</span>
                  <button className="btn btn-ghost" style={{ width: 'auto', color: 'var(--accent)', padding: 0 }} onClick={() => clear('magic')}>
                    Use Magic Link
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}
